import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Globe, Heart, Shield, Users, Lightbulb, Target } from "lucide-react";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const VALUES = [
  { icon: Heart, title: "Human First", desc: "Technology should serve people, not the other way around. Every feature begins with empathy." },
  { icon: Globe, title: "Globally Inclusive", desc: "113 languages, cultural awareness, and heritage-sensitive design — because AI should understand everyone." },
  { icon: Shield, title: "Radically Private", desc: "Your conversations are encrypted, your data is yours, and we will never sell your information. Period." },
  { icon: Users, title: "Family Centered", desc: "Age-appropriate AI with parental controls, because the next generation deserves thoughtful technology." },
  { icon: Lightbulb, title: "Wisdom Over Hype", desc: "We chose 22 historical mentors — not trending influencers. Real wisdom has stood the test of time." },
  { icon: Target, title: "Measurable Impact", desc: "Mood tracking and goal streaks — because self-improvement should be visible and rewarding." },
];

const TIMELINE = [
  { year: "Vision", text: "The idea: an AI companion that transcends language, culture, and age — serving every human, everywhere." },
  { year: "Research", text: "Deep partnerships with psychologists, cultural consultants, and linguists across 6 continents shaped our approach." },
  { year: "Build", text: "Six specialized AI modules, a 22-mentor Wisdom Council, and support for 113 languages brought to life." },
  { year: "Launch", text: "Omni launches globally under Masters Global Inc. — available as a web app, PWA, and native mobile application." },
];

export default function AboutPage() {
  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 px-6 lg:px-12 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[500px]" style={{ background: "radial-gradient(circle, rgba(245,166,35,0.08) 0%, transparent 65%)" }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-6">Our Story</p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05] mb-8"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Built for <span className="text-[#F5A623]">everyone</span>,<br />
              everywhere
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-[#A1A5B3] max-w-2xl leading-relaxed">
              Omni was born from a simple question: what if every person on Earth had access to a wise, 
              patient, culturally-aware companion — available 24/7, in their own language, at a price they could afford?
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ MISSION STATEMENT ═══════ */}
      <section className="py-24 px-6 lg:px-12" style={{ background: "#0A0E1A" }}>
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="rounded-2xl p-10 md:p-14 border" style={{ borderColor: "rgba(245,166,35,0.12)", background: "rgba(245,166,35,0.02)" }}>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-6">Mission</p>
              <blockquote className="text-2xl sm:text-3xl font-light text-white leading-snug tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                "To democratize emotional intelligence, cultural wisdom, and personal growth through AI — 
                making the world's best companion accessible to every human, regardless of language, location, or circumstance."
              </blockquote>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ VALUES GRID ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="about-values">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-4">Values</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-16"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              What we believe
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 60} className="rounded-2xl p-7 border"
                style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <v.icon className="w-5 h-5 text-[#E31212] mb-5" strokeWidth={1.5} />
                <h3 className="text-base font-medium tracking-tight mb-2 text-white">{v.title}</h3>
                <p className="text-sm text-[#6E7385] leading-relaxed">{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ JOURNEY TIMELINE ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" style={{ background: "#0A0E1A" }}>
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-4">Journey</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-16"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              How we got here
            </h2>
          </Reveal>

          <div className="space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 100}>
                <div className="flex gap-8 pb-12 relative">
                  {/* Line */}
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: i === TIMELINE.length - 1 ? "#E31212" : "#F5A623" }} />
                    {i < TIMELINE.length - 1 && <div className="w-px flex-1 mt-2" style={{ background: "rgba(255,255,255,0.08)" }} />}
                  </div>
                  <div className="pb-2">
                    <p className="text-sm font-bold uppercase tracking-[0.15em] mb-2" style={{ color: i === TIMELINE.length - 1 ? "#E31212" : "#F5A623" }}>{t.year}</p>
                    <p className="text-base text-[#A1A5B3] leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ MASTER GLOBAL INC. ═══════ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8" style={{ borderColor: "rgba(245,166,35,0.3)", background: "rgba(245,166,35,0.04)" }}>
              <span className="w-2 h-2 rounded-full bg-[#F5A623]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#F5A623]">A Masters Global Inc. Product</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-lg text-[#A1A5B3] leading-relaxed max-w-2xl mx-auto mb-8">
              Omni is developed and maintained by <a href="https://masterglobalinc.com" target="_blank" rel="noopener noreferrer" className="text-[#F5A623] hover:text-[#FFD080] transition-colors underline underline-offset-4">Masters Global Inc.</a>, 
              a technology company committed to building human-centered AI products that serve the global community.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/auth" data-testid="about-cta-btn">
              <button className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-medium text-base transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: "#E31212", boxShadow: "0 0 30px rgba(227,18,18,0.3)" }}>
                Try Omni Free <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
